import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import portalApi from '../../../../lib/portalApi';
import {
  Award,
  Search,
  Building2,
  CheckCircle2,
  Clock,
  ArrowRight,
  UserPlus,
  RefreshCw,
  AlertCircle,
  Filter,
} from 'lucide-react';

export default function AdminMentorsPage() {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await portalApi.getAdminMentors();
      setMentors(res?.mentors || []);
    } catch (err) {
      console.error('Failed to load mentors:', err);
      setError(err.message || 'Failed to load mentors directory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredMentors = useMemo(() => {
    return mentors.filter((m) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        (m.name && m.name.toLowerCase().includes(q)) ||
        (m.email && m.email.toLowerCase().includes(q));

      const matchStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && m.isActive !== false) ||
        (statusFilter === 'inactive' && m.isActive === false);

      return matchSearch && matchStatus;
    });
  }, [mentors, search, statusFilter]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Award className="w-6 h-6 text-[#ff8ec5]" />
            <span>Mentors</span>
          </h1>
          <p className="text-white/50 text-sm mt-0.5">
            Directory of accelerator mentors, assigned startups, and official performance tracking.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="p-2.5 rounded-xl border border-white/10 bg-white/[0.02] text-white/70 hover:text-white hover:border-white/20 transition-all text-sm flex items-center gap-2"
            title="Refresh mentors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-[#ff8ec5]' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <Link
            to="/admin/invites?role=mentor"
            className="px-4 py-2.5 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white font-medium text-sm transition-all shadow-lg shadow-[#d42b92]/20 flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            <span>Invite Mentor</span>
          </Link>
        </div>
      </div>

      {/* Error state with actionable Retry */}
      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={load}
            className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-semibold transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Search mentors by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-xl text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#d42b92] transition-colors"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-white/40 hidden sm:block" />
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                statusFilter === 'all'
                  ? 'bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40'
                  : 'bg-black/30 text-white/60 border border-white/10 hover:text-white'
              }`}
            >
              All ({mentors.length})
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                statusFilter === 'active'
                  ? 'bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40'
                  : 'bg-black/30 text-white/60 border border-white/10 hover:text-white'
              }`}
            >
              Active ({mentors.filter((m) => m.isActive !== false).length})
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('inactive')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                statusFilter === 'inactive'
                  ? 'bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40'
                  : 'bg-black/30 text-white/60 border border-white/10 hover:text-white'
              }`}
            >
              Inactive ({mentors.filter((m) => m.isActive === false).length})
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="p-16 text-center text-white/40 space-y-3">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#ff8ec5]" />
          <p className="text-sm">Loading mentors directory…</p>
        </div>
      ) : mentors.length === 0 ? (
        <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
          <Award className="w-12 h-12 mx-auto text-white/20" />
          <h3 className="text-white font-medium text-base">No mentors registered yet</h3>
          <p className="text-white/40 text-sm max-w-sm mx-auto">
            Invite your first accelerator mentor to begin assigning startups and performance metrics.
          </p>
          <Link
            to="/admin/invites?role=mentor"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#d42b92] text-white text-xs font-semibold hover:bg-[#b02279] transition-colors mt-2"
          >
            <UserPlus className="w-4 h-4" />
            <span>Invite Mentor</span>
          </Link>
        </div>
      ) : filteredMentors.length === 0 ? (
        <div className="p-12 text-center rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
          <Search className="w-10 h-10 mx-auto text-white/20" />
          <h3 className="text-white font-medium text-base">No matching mentors found</h3>
          <p className="text-white/40 text-sm">
            No mentors match your search query “{search}” or selected filter.
          </p>
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setStatusFilter('all');
            }}
            className="px-3.5 py-1.5 rounded-xl border border-white/10 text-white/70 hover:text-white text-xs transition-colors"
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredMentors.map((m) => {
            const initials = (m.name || m.email || 'M')
              .split(' ')
              .map((n) => n[0])
              .join('')
              .slice(0, 2)
              .toUpperCase();

            return (
              <div
                key={m.id}
                className="p-4 sm:p-5 rounded-2xl bg-[#08080A] border border-[#2C2C34] hover:border-[#D42B92]/40 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 group"
              >
                {/* Mentor Info */}
                <div className="flex items-center gap-3.5 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#d42b92]/30 to-[#321129] border border-[#d42b92]/40 text-[#ff8ec5] flex items-center justify-center font-bold text-sm shrink-0">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/mentors/${m.id}`}
                        className="text-white font-semibold text-base hover:text-[#ff8ec5] transition-colors truncate"
                      >
                        {m.name || 'Unnamed Mentor'}
                      </Link>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                          m.isActive !== false
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'
                        }`}
                      >
                        {m.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="text-white/40 text-xs truncate mt-0.5">{m.email}</div>
                  </div>
                </div>

                {/* Counts & Performance Metrics */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs">
                  {/* Startups Count */}
                  <div className="px-3 py-1.5 rounded-xl bg-white/[0.03] border border-white/5 flex items-center gap-1.5 text-white/70">
                    <Building2 className="w-3.5 h-3.5 text-[#ff8ec5]" />
                    <span>
                      <strong className="text-white font-semibold">
                        {m.assignedStartupsCount || 0}
                      </strong>{' '}
                      Startups
                    </span>
                  </div>

                  {/* Pending Tasks */}
                  <div className="px-3 py-1.5 rounded-xl bg-white/[0.03] border border-white/5 flex items-center gap-1.5 text-white/70">
                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                    <span>
                      <strong className="text-white font-semibold">{m.pendingTasks || 0}</strong>{' '}
                      Pending
                    </span>
                  </div>

                  {/* KRA/KPI Status */}
                  <div className="px-3 py-1.5 rounded-xl bg-white/[0.03] border border-white/5 flex items-center gap-1.5 text-white/70">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    <span>
                      <strong className="text-white font-semibold">
                        {m.kraKpiDone ?? m.completedTasks ?? 0}
                      </strong>
                      /{m.kraKpiTotal ?? (m.completedTasks + m.pendingTasks) ?? 0} KRA/KPI
                    </span>
                  </div>

                  {/* View Mentor Action */}
                  <Link
                    to={`/admin/mentors/${m.id}`}
                    className="ml-auto sm:ml-2 px-3.5 py-2 rounded-xl bg-white/[0.05] hover:bg-[#d42b92] text-white/80 hover:text-white font-medium text-xs transition-all flex items-center gap-1.5 border border-white/10 hover:border-[#d42b92] group-hover:bg-[#d42b92] group-hover:text-white shadow-sm"
                  >
                    <span>View Mentor</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
